import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { useMember } from "../lib/memberAuth";
import { SocialButtons } from "../components/account/SocialButtons";

const SignupPrompt = () => {
  const { member } = useMember();
  if (member) return null;
  return (
    <div className="mt-6 bg-white border border-black/10 rounded-2xl p-6 text-center" data-testid="signup-prompt">
      <p className="font-display font-bold">Save time next time</p>
      <p className="text-sm text-black/50 mt-1 mb-4">Create your free One Love account to view your receipts, tickets and orders in one place.</p>
      <SocialButtons intent="login" />
      <Link to="/account/login" className="block text-sm text-[#E63946] font-medium mt-3 hover:underline" data-testid="signup-email-link">Create with Email</Link>
    </div>
  );
};

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${orderId}`);
        if (stop) return;
        if (data.paid) { setOrder(data.order); setState("paid"); clear(); return; }
        setOrder(data.order);
      } catch (e) { console.warn("Order status check failed, retrying…", e); }
      tries.current += 1;
      if (tries.current < 8 && !stop) setTimeout(poll, 2500);
      else if (!stop) setState("failed");
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [orderId]);

  const row = (label, val, opts = {}) => (
    <div className={`flex justify-between text-sm py-1 ${opts.bold ? "font-bold text-base border-t border-black/10 pt-3 mt-2" : ""}`}>
      <span className={opts.bold ? "" : "text-black/60"}>{label}</span><span>{val}</span>
    </div>
  );

  return (
    <div className="pt-[72px] min-h-screen bg-[#FAFAFA]" data-testid="order-success-page">
      <div className="max-w-xl mx-auto px-5 py-16">
        {state === "checking" && (
          <div className="text-center py-24">
            <Loader2 className="animate-spin text-[#E63946] mx-auto mb-4" size={36} />
            <p className="font-display font-bold text-xl">Confirming your payment…</p>
            <p className="text-black/50 text-sm mt-2">Please don't close this page.</p>
          </div>
        )}
        {state === "failed" && (
          <div className="text-center py-24" data-testid="order-failed">
            <XCircle className="text-[#E63946] mx-auto mb-4" size={40} />
            <p className="font-display font-bold text-xl mb-2">We couldn't confirm your payment</p>
            <p className="text-black/50 text-sm mb-6">If you completed payment, check your email for a receipt — or contact us at 501-414-2963.</p>
            <Link to="/checkout" className="rounded-full bg-[#E63946] text-white px-7 py-3 text-sm font-medium">Back to Checkout</Link>
          </div>
        )}
        {state === "paid" && order && (
          <div className="bg-white border border-black/10 rounded-2xl p-8" data-testid="order-receipt">
            <div className="text-center mb-6">
              <CheckCircle2 className="text-emerald-500 mx-auto mb-3" size={44} />
              <h1 className="font-display font-extrabold text-2xl">{Number(order.total) === 0 ? "You're Registered!" : "Thank You for Your Order!"}</h1>
              <p className="text-black/50 text-sm mt-1">Order <span className="font-mono font-medium text-black">{order.order_number}</span> — a receipt has been emailed to {order.email}.</p>
            </div>
            <div className="space-y-1 mb-2">
              {order.items?.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1">
                  <span>{i.name} × {i.qty}</span><span>${(i.unit * i.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-black/10 pt-3">
              {row("Subtotal", `$${Number(order.subtotal).toFixed(2)}`)}
              {Number(order.discount) > 0 && row(`Discount${order.promo_code ? ` (${order.promo_code})` : ""}`, `-$${Number(order.discount).toFixed(2)}`)}
              {Number(order.service_fee) > 0 && row(order.service_fee_label || "Service Fee", `$${Number(order.service_fee).toFixed(2)}`)}
              {Number(order.shipping) > 0 && row("Shipping", `$${Number(order.shipping).toFixed(2)}`)}
              {Number(order.tax) > 0 && row("Sales Tax", `$${Number(order.tax).toFixed(2)}`)}
              {row("Total Paid", `$${Number(order.total).toFixed(2)}`, { bold: true })}
            </div>
            {order.ticket_code && (
              <div className="text-center mt-6 border-t border-black/10 pt-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#E63946] mb-3">Your Ticket Pass</p>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/paypal/tickets/qr/${order.ticket_code}.png`} alt="Ticket QR"
                  className="w-44 h-44 mx-auto border border-black/10 rounded-xl p-2" data-testid="ticket-qr" />
                <p className="font-mono font-bold tracking-[0.2em] mt-2">{order.ticket_code}</p>
                <p className="text-xs text-black/45 mt-1">Show this QR code at the door — it was also emailed to you.</p>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-8">
              <Link to="/" className="rounded-full border border-black/15 px-6 py-3 text-sm hover:bg-black/5">Back Home</Link>
              <Link to="/events" className="rounded-full bg-[#E63946] text-white px-6 py-3 text-sm font-medium hover:bg-[#BA1B25]">View Events</Link>
            </div>
          </div>
        )}
        {state === "paid" && <SignupPrompt />}
      </div>
    </div>
  );
}
